import {
  AddressRoutingTypeEnum,
  getAddressRoutingTypeOfCurrency,
  getAddressTypeDrawerOptions,
  getCurrencyDrawerOptions,
  getCustomerDrawerOptions,
  getMarketAccountOptions,
  ModeEnum,
  type ColumnDef,
  type Currency,
  type Customer,
  type ICustomerAddress,
  type ICustomerRoutingInfo,
  type InputsAndDropdownsDrawerOption,
  type MarketAccount,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import type React from 'react';
import { ConfirmAddressStatusField } from './ConfirmAddressStatusField';
import type { CustomerAddressesDrawerEntity } from './index';

export function getCustomerAddressesColumns(
  RevealRoutingInfoRenderer: (params: ICellRendererParams) => React.ReactNode,
  routingInfoMap: Map<string, ICustomerRoutingInfo>
): ColumnDef<ICustomerAddress>[] {
  return [
    { type: 'addressStatus', field: 'Status' },
    { type: 'date', field: 'Timestamp', sortable: true, initialSort: '-' },
    { type: 'transactionType', field: 'AddressType', initialWidth: 150, sortable: true },
    { type: 'currency', field: 'Currency', initialWidth: 150 },
    { type: 'text', field: 'Name' },
    { type: 'text', field: 'CustomerAddressID', initialHide: true },
    { type: 'counterparty', field: 'Counterparty' },
    { type: 'marketAccount', field: 'MarketAccount', title: 'Market Account', id: 'marketAccount', initialHide: true },
    {
      type: 'marketAccountSourceAccountID',
      field: 'MarketAccount',
      id: 'customerAccountID',
      initialHide: true,
    },
    { type: 'text', field: 'CustomerUser', initialHide: true },
    {
      title: 'Wallet Address',
      id: 'WalletAddress',
      type: 'custom',
      params: {
        cellRenderer: RevealRoutingInfoRenderer,
        valueGetter: ({ data }: ValueGetterParams<ICustomerAddress>) =>
          data && routingInfoMap.get(data.CustomerAddressID)?.WalletAddress,
      },
    },
    {
      title: 'Memo',
      id: 'Memo',
      type: 'custom',
      params: {
        cellRenderer: RevealRoutingInfoRenderer,
        valueGetter: ({ data }: ValueGetterParams<ICustomerAddress>) =>
          data && routingInfoMap.get(data.CustomerAddressID)?.Memo,
      },
    },
  ];
}

export function getCustomerAddressDrawerOptions({
  customers,
  currencies,
  marketAccounts,
}: {
  customers: Customer[];
  currencies: Currency[];
  marketAccounts: MarketAccount[];
}): InputsAndDropdownsDrawerOption<CustomerAddressesDrawerEntity>[] {
  const currenciesBySymbol = new Map(currencies.map(currency => [currency.Symbol, currency]));
  return [
    {
      field: 'Status',
      type: 'custom',
      title: 'Status',
      useCustomFormGroup: true,
      getIsHidden: (_form, originalEntity) => originalEntity == null,
      renderer: props =>
        props.originalEntity && <ConfirmAddressStatusField {...props} originalEntity={props.originalEntity} />,
    },
    {
      field: 'Counterparty',
      type: 'dropdown',
      title: 'Counterparty',
      getDropdownOptions: () => getCustomerDrawerOptions(customers),
      getIsRequired: () => true,
      onChange: (_newValue, onFormUpdate) => onFormUpdate('MarketAccount', ''),
    },
    {
      field: 'MarketAccount',
      type: 'dropdown',
      title: 'Market Account',
      getIsDisabled: form => !form.Counterparty,
      getDropdownOptions: form => {
        const filteredMarketAccounts = marketAccounts.filter(
          marketAccount => marketAccount.Counterparty === form.Counterparty
        );
        return getMarketAccountOptions(filteredMarketAccounts);
      },
    },
    {
      field: 'Currency',
      type: 'dropdown',
      title: 'Currency',
      getDropdownOptions: () => getCurrencyDrawerOptions(currencies),
      getIsRequired: () => true,
    },
    {
      field: 'AddressType',
      type: 'dropdown',
      title: 'Address Type',
      getDropdownOptions: () => getAddressTypeDrawerOptions(),
      getIsRequired: () => true,
    },

    {
      field: 'Name',
      type: 'input',
      title: 'Name of Address',
      getIsRequired: () => true,
      placeholder: 'Name...',
      getIsDisabled: entity => !entity.Currency,
    },
    {
      field: '_WalletAddress',
      type: 'input',
      title: 'Deposit Address',
      getIsRequired: () => true,
      placeholder: 'Address...',
      getIsHidden: entity =>
        getAddressRoutingTypeOfCurrency(currenciesBySymbol.get(entity.Currency)) === AddressRoutingTypeEnum.Fiat,
      getIsDisabled: entity => !entity.Currency,
    },
    {
      field: '_Memo',
      type: 'input',
      title: 'Memo of Address',
      placeholder: 'Memo...',
      getIsDisabled: entity => !entity.Currency,
    },
  ];
}

export function getCustomerAddressesPostEntity(
  entity: CustomerAddressesDrawerEntity,
  currency?: Currency
): ICustomerAddress {
  const {
    _WalletAddress,
    _Memo,
    // Default by extracting the default AddressRoutingType for this currency.
    AddressRoutingType = getAddressRoutingTypeOfCurrency(currency),
    ...rest
  } = entity;
  return {
    ...rest,
    AddressRoutingType,
    RoutingInfo: {
      // [DEAL-3250] WalletAddress is not sent for fiat currencies.
      WalletAddress: AddressRoutingType === AddressRoutingTypeEnum.Fiat ? '' : _WalletAddress,
      Memo: _Memo ?? '',
      // We currently just send this field as an empty string for all currencies.
      DestinationTag: '',
      ExternalTxnURL: '',
    },
    Mode: ModeEnum.Enabled,
  };
}
